import type {
  AdminHomeBanner,
  Body_create_home_banner_api_v1_admin_home_banners_post,
  Body_update_home_banner_api_v1_admin_home_banners__banner_id__put,
} from 'client';
import {addDays, setHours, startOfDay} from 'date-fns';
import {DEFAULT_LIGHT_BANNER_COLOR, isHexColor, suggestDarkColor} from 'views/home/bannerColors';

import {dateToWallClock, isWallClock} from './bannerSchedule';

/**
 * An image slot in the form. A new file stays in the browser until Create or
 * Save sends it with the rest of the form.
 */
export type ImageSlot =
  | {kind: 'none'}
  | {kind: 'stored'; path: string}
  | {kind: 'new'; file: File; previewUrl: string};

export type BannerDraft = {
  title: string;
  subtitle: string;
  scrolling_strip: string;
  cta_label: string;
  cta_url: string;
  light_background_color: string;
  /** Empty for no dark-mode look. */
  dark_background_color: string;
  fte_only: boolean;
  use_local_time: boolean;
  /** Wall-clock readings, "YYYY-MM-DDTHH:mm:ss". */
  starts_at: string;
  ends_at: string;
  image: ImageSlot;
  dark_image: ImageSlot;
};

export const LIMITS = {title: 200, copy: 500, ctaLabel: 100, ctaUrl: 2048} as const;

/** A banner starting tomorrow at 9 AM and running a week. */
export const newBannerDraft = (now = new Date()): BannerDraft => {
  const start = setHours(startOfDay(addDays(now, 1)), 9);
  return {
    title: '',
    subtitle: '',
    scrolling_strip: '',
    cta_label: '',
    cta_url: '',
    light_background_color: DEFAULT_LIGHT_BANNER_COLOR,
    dark_background_color: suggestDarkColor(DEFAULT_LIGHT_BANNER_COLOR),
    fte_only: false,
    use_local_time: false,
    starts_at: dateToWallClock(start),
    ends_at: dateToWallClock(addDays(start, 7)),
    image: {kind: 'none'},
    dark_image: {kind: 'none'},
  };
};

const storedSlot = (path?: string | null): ImageSlot =>
  path ? {kind: 'stored', path} : {kind: 'none'};

export const draftFromBanner = (banner: AdminHomeBanner): BannerDraft => ({
  title: banner.title,
  subtitle: banner.subtitle ?? '',
  scrolling_strip: banner.scrolling_strip ?? '',
  cta_label: banner.cta_label ?? '',
  cta_url: banner.cta_url ?? '',
  light_background_color: (
    banner.light_background_color ?? DEFAULT_LIGHT_BANNER_COLOR
  ).toUpperCase(),
  dark_background_color: (banner.dark_background_color ?? '').toUpperCase(),
  fte_only: banner.fte_only,
  use_local_time: banner.use_local_time,
  starts_at: banner.starts_at,
  ends_at: banner.ends_at,
  image: storedSlot(banner.image_path),
  dark_image: storedSlot(banner.dark_image_path),
});

export type DraftErrors = Partial<
  Record<
    'title' | 'subtitle' | 'scrolling_strip' | 'cta' | 'colors' | 'schedule' | 'images',
    string
  >
>;

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.host);
  } catch {
    return false;
  }
};

/** The same rules the API applies, so most mistakes show before anything is sent. */
export const validateDraft = (draft: BannerDraft): DraftErrors => {
  const errors: DraftErrors = {};
  const title = draft.title.trim();
  const ctaLabel = draft.cta_label.trim();
  const ctaUrl = draft.cta_url.trim();

  if (!title) {
    errors.title = 'Add a title.';
  } else if (title.length > LIMITS.title) {
    errors.title = `Keep the title to ${LIMITS.title} characters.`;
  }
  if (draft.subtitle.trim().length > LIMITS.copy) {
    errors.subtitle = `Keep the subtitle to ${LIMITS.copy} characters.`;
  }
  if (draft.scrolling_strip.trim().length > LIMITS.copy) {
    errors.scrolling_strip = `Keep the scrolling strip to ${LIMITS.copy} characters.`;
  }

  if (Boolean(ctaLabel) !== Boolean(ctaUrl)) {
    errors.cta = 'A button needs both a label and a link.';
  } else if (ctaUrl && !isHttpUrl(ctaUrl)) {
    errors.cta = 'Use a full link that starts with https://.';
  } else if (ctaLabel.length > LIMITS.ctaLabel || ctaUrl.length > LIMITS.ctaUrl) {
    errors.cta = 'The button label or link is too long.';
  }

  if (
    !isHexColor(draft.light_background_color) ||
    (draft.dark_background_color !== '' && !isHexColor(draft.dark_background_color))
  ) {
    errors.colors = 'Colors need six hex digits, like #1A2B3C.';
  }

  if (!isWallClock(draft.starts_at) || !isWallClock(draft.ends_at)) {
    errors.schedule = 'Choose a start and an end.';
  } else if (draft.ends_at <= draft.starts_at) {
    // Readings share one fixed format, so text order is time order.
    errors.schedule = 'The end needs to be after the start.';
  }

  if (draft.dark_image.kind !== 'none' && draft.image.kind === 'none') {
    errors.images = 'Add a light-mode image to go with the dark-mode one.';
  }

  return errors;
};

/** Optional text left blank is left out, which the API stores as empty. */
const optional = (value: string) => value.trim() || undefined;

const fieldsOf = (draft: BannerDraft) => ({
  title: draft.title.trim(),
  subtitle: optional(draft.subtitle),
  scrolling_strip: optional(draft.scrolling_strip),
  cta_label: optional(draft.cta_label),
  cta_url: optional(draft.cta_url),
  light_background_color: draft.light_background_color,
  dark_background_color: optional(draft.dark_background_color),
  fte_only: draft.fte_only,
  use_local_time: draft.use_local_time,
  starts_at: draft.starts_at,
  ends_at: draft.ends_at,
});

const newFile = (slot: ImageSlot) => (slot.kind === 'new' ? slot.file : undefined);

export const toCreateBody = (
  draft: BannerDraft
): Body_create_home_banner_api_v1_admin_home_banners_post => ({
  ...fieldsOf(draft),
  image: newFile(draft.image),
  dark_image: newFile(draft.dark_image),
});

/**
 * Every field, as the update replaces them all. Stored images are kept unless
 * the form replaced or removed them.
 */
export const toUpdateBody = (
  draft: BannerDraft
): Body_update_home_banner_api_v1_admin_home_banners__banner_id__put => ({
  ...fieldsOf(draft),
  image: newFile(draft.image),
  dark_image: newFile(draft.dark_image),
  remove_image: draft.image.kind === 'none' || undefined,
  remove_dark_image: draft.dark_image.kind === 'none' || undefined,
});

const pad = (value: number) => value.toString().padStart(2, '0');

/**
 * Change the date of a wall-clock reading and keep its time, or the reverse.
 * These edit the text directly: going through a Date would let the admin's own
 * DST change shift a reading like 2:30 AM.
 */
export const withDate = (value: string, day: Date) =>
  `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}${value.slice(10)}`;

export const withMinutesOfDay = (value: string, minutes: number) =>
  `${value.slice(0, 11)}${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`;

export const minutesOfDay = (value: string) =>
  Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16));
